import React from "react"
import { BodyCopyRegular, Box, SubHeadline, BoxProps } from "@theguarantors/ui-kit-v2/dist/design-system/typography"
import { Container, First, Second } from "./Footer.styles"

import {
  FooterSection,
  FooterContainer,
  FooterContent,
  FooterMainContentGrid,
  FooterLogo,
  FooterCTA,
  FooterDesktopImg,
  FooterMobileImg,
  FooterExplore,
  FooterCompany,
  FooterSocial,
  FooterLinksColumn,
  FooterSignInButton,
  FooterSocialLinks,
  LinkColumnHeading,
  LinkColumnLinkItem,
  LinkColumnLinkItemTitle,
  SocialMediaItem,
  FooterSecondaryContent,
  FooterLegalDisclaimer,
  FooterLegalLink,
  FooterLegalContent,
  FooterLegalCopyright,
  FooterLegalLinks,
  FooterLegalLinkSecondary,
  FooterSecondaryContentDisclaimer,
  FooterBCorpWrapper,
} from "./FooterV2.styles"

import { NavLinks, NavSection, SocialLinks } from "../LandingPage.styles"
import {
  LinkedinIcon,
  TwitterIcon,
  FacebookIcon,
  FacebookRoundIcon,
  InstagramIcon,
  WeeboIcon,
  WechatIcon,
} from "../../../components/icons"

import WhiteLogo from "../../../assets/WhiteLogo.svg"
import BCorpLarge from "../../../assets/b-corp-large.png"
import BCorpSmall from "../../../assets/b-corp-small.png"

import config, { linkTitles } from "../../../config"
import { FeatureFlag, useFeatureFlag } from "../../../hooks/use-feature-flag"

const anchorProps: BoxProps = {
  color: "#fff",
  as: "a",
  size: "l",
  style: { textDecoration: "none" },
}
const goToUrl = (url: string) => () => (window.location.href = url)

const SocialMediaList = [
  {
    url: "https://www.linkedin.com/company/the-guarantors",
    icon: <LinkedinIcon width="30" height="30" />,
    label: "LinkedIn",
  },
  {
    url: "https://twitter.com/The_Guarantors",
    icon: <TwitterIcon width="30" height="30" />,
    label: "Twitter",
  },
  {
    url: "https://www.facebook.com/TheGuarantors",
    icon: <FacebookRoundIcon width="18" height="18" />,
    label: "Facebook",
  },
  {
    url: "https://www.instagram.com/theguarantors",
    icon: <InstagramIcon width="20" height="20" />,
    label: "Instagram",
  },
  {
    url: "https://www.theguarantors.com/wechat",
    icon: <WechatIcon width="20" height="20" />,
    label: "WeChat",
  },
]

const bCorpLinkUrl = "https://www.theguarantors.com/blog/company-updates/theguarantors-announces-b-corp-certification"

export const Footer: React.FC = () => {
  const [newGetStartedPage] = useFeatureFlag(FeatureFlag.NEW_GET_STARTED_PAGE)
  const [enableTrustCenter] = useFeatureFlag(FeatureFlag.ENABLE_FOOTER_TRUST_CENTER)
  const [enableBlogRename] = useFeatureFlag(FeatureFlag.ENABLE_FOOTER_BLOG_RENAME)
  const [enableMarketPlace] = useFeatureFlag(FeatureFlag.ENABLE_FOOTER_MARKETPLACE)
  const [enableContactRemove] = useFeatureFlag(FeatureFlag.REMOVE_FOOTER_CONTACT_US)

  const newGetStartedPageFeatureFlag: boolean = newGetStartedPage === "on"
  const enableTrustCenterFlag: boolean = enableTrustCenter === "on"
  const enableBlogRenameFlag: boolean = enableBlogRename === "on"
  const footerMarketplaceFlag: boolean = enableMarketPlace === "on"
  const contactRemoveFlag: boolean = enableContactRemove === "on"

  const exploreLinks = [
    { link: config.renterPath, title: linkTitles.renters },
    { link: config.operatorsPath, title: linkTitles.operators },
    { link: config.brokerPath, title: linkTitles.broker },
    { link: config.faqPath, title: linkTitles.faq },
    { link: config.theWelcomeMatPath, title: enableBlogRenameFlag ? linkTitles.blog : linkTitles.theWelcomeMat },
    ...(footerMarketplaceFlag ? [{ link: config.partnersPath, title: linkTitles.partners }] : []),
  ]

  const companyLinks = [
    { link: config.aboutPath, title: linkTitles.about },
    ...(contactRemoveFlag ? [] : [{ link: config.contactPath, title: linkTitles.contact }]),
    { link: config.careerPath, title: linkTitles.careers },
    { link: config.pressPath, title: linkTitles.press },
  ]

  const secondaryFooterLinks = [
    {
      link: config.privacyPath,
      title: linkTitles.privacy,
    },
    {
      link: config.tosPath,
      title: linkTitles.tos,
    },
    {
      link: config.legalRegulatoryPath,
      title: linkTitles.legalRegulatory,
    },
    {
      link: config.noticeOfCollectionPath,
      title: linkTitles.noticeOfCollection,
    },
    ...(enableTrustCenterFlag
      ? [
          {
            link: config.trustCenterPath,
            title: linkTitles.trustCenter,
          },
        ]
      : []),
  ]

  if (newGetStartedPageFeatureFlag) {
    return (
      <FooterSection className="footer">
        <FooterContainer className="footer__content-wrapper">
          <FooterContent className="footer__content">
            <FooterMainContentGrid className="footer-main-content__grid">
              <FooterLogo className="footer-logo">
                <a title="Home" href="/">
                  <img src={WhiteLogo} alt="logo" />
                </a>
              </FooterLogo>
              <FooterCTA className="footer-cta">
                <div className="footer-main-content__cta-wrapper">
                  <FooterSignInButton
                    variant={"v2_primary"}
                    type="submit"
                    onClick={goToUrl(config.signinPath)}
                    newGetStartedPageFeatureFlag={newGetStartedPageFeatureFlag}
                    fontFamily="acid"
                  >
                    Sign In
                  </FooterSignInButton>
                </div>
                <FooterBCorpWrapper>
                  {/**
                   * Per OTTERS-1947, it was explicitely asked to NOT add `rel="noopener noreferrer"` to the link
                   */}
                  {/* eslint-disable-next-line react/jsx-no-target-blank */}
                  <a title="B Corp Certification" href={bCorpLinkUrl} target="_blank">
                    <FooterDesktopImg src={BCorpLarge} className="desktop-img" alt="B Corp Certification" />
                    <FooterMobileImg src={BCorpSmall} className="mobile-img" alt="B Corp Certification" />
                  </a>
                </FooterBCorpWrapper>
              </FooterCTA>

              <FooterExplore className="footer-explore">
                <FooterLinksColumn className="footer-main-content__links footer-main-content__links--secondary">
                  <LinkColumnHeading>Explore</LinkColumnHeading>
                  {exploreLinks.map((item) => (
                    <LinkColumnLinkItem key={item.link} href={item.link}>
                      <LinkColumnLinkItemTitle>{item.title}</LinkColumnLinkItemTitle>
                    </LinkColumnLinkItem>
                  ))}
                </FooterLinksColumn>
              </FooterExplore>
              <FooterCompany className="footer-company">
                <FooterLinksColumn className="footer-main-content__links footer-main-content__links--secondary">
                  <LinkColumnHeading>Company</LinkColumnHeading>
                  {companyLinks.map((item) => (
                    <LinkColumnLinkItem key={item.link} href={item.link}>
                      <LinkColumnLinkItemTitle>{item.title}</LinkColumnLinkItemTitle>
                    </LinkColumnLinkItem>
                  ))}
                </FooterLinksColumn>
              </FooterCompany>
              <FooterSocial className="footer-social">
                <FooterSocialLinks className="footer-main-content__social-media-links" aria-label="Social media links">
                  {SocialMediaList.map(({ url, icon, label }) => (
                    <SocialMediaItem key={url} aria-label={label} href={url}>
                      {icon}
                    </SocialMediaItem>
                  ))}
                </FooterSocialLinks>
              </FooterSocial>
            </FooterMainContentGrid>
          </FooterContent>
        </FooterContainer>
        <FooterContainer className="footer__content-wrapper" bottom>
          <FooterContent className="footer__content">
            <FooterSecondaryContent className="footer-secondary-content">
              <FooterSecondaryContentDisclaimer className="footer-secondary-content__disclaimer">
                <div className="footer-secondary-content__legal">
                  <FooterLegalDisclaimer className="footer-secondary-content__paragraph">
                    TheGuarantors is a licensed insurance agent and broker. For more detailed information, please see
                    our&nbsp;
                    <FooterLegalLink
                      className="link link--decorated footer-secondary-content__paragraph-link"
                      href="/legal-regulatory"
                    >
                      <LinkColumnLinkItemTitle className="link__content" decorated>
                        Legal/Regulatory Notice
                      </LinkColumnLinkItemTitle>
                    </FooterLegalLink>
                    . CA: Guarantors Insurance Agency, Lic. No. 0M44435. AZ, CT, DE, KS, ME, MI, MT, NE, NH, NJ, NC, ND,
                    OH, OK, OR, SC, WV: The Guarantors Agency. All coverages are subject to the terms, conditions and
                    exclusions of the actual policy issued. Not all policies or coverages are available in every state.
                  </FooterLegalDisclaimer>
                </div>
              </FooterSecondaryContentDisclaimer>
              <FooterLegalContent className="footer-secondary-content__bottom">
                <FooterLegalCopyright className="footer-secondary-content__copyright">
                  © 2024 TheGuarantors
                </FooterLegalCopyright>
                <FooterLegalLinks className="footer-secondary-content__links" aria-label="Links to legal documents">
                  {secondaryFooterLinks.map((item) => (
                    <FooterLegalLinkSecondary className="link footer-secondary-content__link" href={item.link}>
                      <LinkColumnLinkItemTitle className="link__content">{item.title}</LinkColumnLinkItemTitle>
                    </FooterLegalLinkSecondary>
                  ))}
                </FooterLegalLinks>
              </FooterLegalContent>
            </FooterSecondaryContent>
          </FooterContent>
        </FooterContainer>
      </FooterSection>
    )
  }

  return (
    <FooterContainer>
      <Container>
        <First>
          <div>
            <BodyCopyRegular as="p" color="white" maxWidth="34rem">
              We are an Insurtech company that is rethinking insurance products and financial solutions for residential
              and commercial real estate professionals as well as their residents and tenants.
            </BodyCopyRegular>
          </div>
          <nav>
            <NavSection>
              <SubHeadline color="white" size="l">
                TheGuarantors
              </SubHeadline>
              <ul>
                <li>
                  <BodyCopyRegular {...anchorProps} href="/residential">
                    Residential
                  </BodyCopyRegular>
                </li>
                <li>
                  <BodyCopyRegular {...anchorProps} href="/commercial">
                    Commercial
                  </BodyCopyRegular>
                </li>
                <li>
                  <BodyCopyRegular {...anchorProps} href="/renters">
                    Renters
                  </BodyCopyRegular>
                </li>
                <li>
                  <BodyCopyRegular {...anchorProps} href="/brokers">
                    Brokers
                  </BodyCopyRegular>
                </li>
              </ul>
            </NavSection>
            <NavSection>
              <SubHeadline color="white" size="l">
                Company
              </SubHeadline>
              <ul>
                <li>
                  <BodyCopyRegular {...anchorProps} href="/about">
                    About us
                  </BodyCopyRegular>
                </li>
                <li>
                  <BodyCopyRegular {...anchorProps} href="/press">
                    Press
                  </BodyCopyRegular>
                </li>
                <li>
                  <BodyCopyRegular {...anchorProps} href="https://careers.theguarantors.com">
                    Careers
                  </BodyCopyRegular>
                </li>
              </ul>
            </NavSection>
            <NavSection>
              <SubHeadline color="white" size="l">
                Resources
              </SubHeadline>
              <ul>
                <li>
                  <BodyCopyRegular {...anchorProps} href="/blog">
                    Blog
                  </BodyCopyRegular>
                </li>
                <li>
                  <BodyCopyRegular {...anchorProps} href="/faq-renters">
                    FAQ
                  </BodyCopyRegular>
                </li>
                <li>
                  <BodyCopyRegular {...anchorProps} href="https://www.theguarantors.com/resources">
                    Commercial
                  </BodyCopyRegular>
                </li>
                <li>
                  <BodyCopyRegular {...anchorProps} href="https://www.theguarantors.com/resources?mixquery=white-paper">
                    White Papers
                  </BodyCopyRegular>
                </li>
                <li>
                  <BodyCopyRegular {...anchorProps} href="https://www.theguarantors.com/resources?mixquery=case-study">
                    Case Studies
                  </BodyCopyRegular>
                </li>
                <li>
                  <BodyCopyRegular {...anchorProps} href="https://www.theguarantors.com/resources?mixquery=article">
                    Articles
                  </BodyCopyRegular>
                </li>
              </ul>
            </NavSection>
            <NavSection>
              <SubHeadline color="white" size="l">
                Contact Us
              </SubHeadline>
              <ul>
                <li>
                  <BodyCopyRegular {...anchorProps} href="tel:+12122660020">
                    +1 212-266-0020
                  </BodyCopyRegular>
                </li>
                <li>
                  <BodyCopyRegular {...anchorProps} href="mailto:success@theguarantors.com">
                    success
                    <br />
                    @theguarantors.com
                  </BodyCopyRegular>
                </li>
                <li>
                  <BodyCopyRegular as="p" size="l" color="#fff">
                    1 World Trade Center
                    <br /> New York, NY 10007
                  </BodyCopyRegular>
                </li>
              </ul>
            </NavSection>
          </nav>
        </First>
        <Box pt={50} />
        <SocialLinks>
          <LinkedinIcon />
          <TwitterIcon />
          <FacebookIcon />
          <InstagramIcon />
          <WeeboIcon />
          <WechatIcon />
        </SocialLinks>
        <Box pt={30} />
        <Second>
          <div>
            <BodyCopyRegular color="white" size="l">
              TheGuarantors Agency Lease Rental Bonds are issued by The Hanover Insurance Company, an insurance company
              licensed in New York State and A-rated (Excellent) by A.M. Best.
            </BodyCopyRegular>
            <NavLinks>
              <ul>
                <li>
                  <BodyCopyRegular {...anchorProps} href="/terms?hsLang=en">
                    Terms of Service
                  </BodyCopyRegular>
                </li>
                <li>
                  <BodyCopyRegular {...anchorProps} href="/privacy?hsLang=en">
                    Privacy Policy
                  </BodyCopyRegular>
                </li>
                <li>
                  <BodyCopyRegular {...anchorProps} href="/legal-regulatory?hsLang=en">
                    Legal/Regulatory Notice
                  </BodyCopyRegular>
                </li>
                <li>
                  <BodyCopyRegular {...anchorProps} href="https://rebny.com/">
                    Member REBNY
                  </BodyCopyRegular>
                </li>
              </ul>
            </NavLinks>
          </div>
        </Second>
      </Container>
    </FooterContainer>
  )
}
